import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "./Page";
import EntityList from "./EntityList";
import Input from "../elements/Input";
import Select from "../elements/Select";
import DateField from "../elements/Date";
import Pagination from "../elements/Pagination";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersReceiptsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/orders/monitor";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [orderBy, setOrderBy] = useState("receivedAt");
  const [order, setOrder] = useState("desc");
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [filterDraft, setFilterDraft] = useState({
    searchText: "",
    status: "all",
    startDate: null,
    endDate: null,
  });
  const [filterApplied, setFilterApplied] = useState({
    searchText: "",
    status: "all",
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    loadReceipts();
  }, [clientId]);

  const filteredRecords = useMemo(() => {
    const searchText = String(filterApplied.searchText || "")
      .trim()
      .toLowerCase();

    return records
      .filter((row) => {
        if (
          filterApplied.status !== "all" &&
          row?.receiptStatus !== filterApplied.status
        ) {
          return false;
        }

        const receivedAt = row?.receivedAt ? new Date(row.receivedAt) : null;
        if (
          filterApplied.startDate &&
          receivedAt &&
          receivedAt < new Date(filterApplied.startDate)
        ) {
          return false;
        }
        if (
          filterApplied.endDate &&
          receivedAt &&
          receivedAt > new Date(filterApplied.endDate)
        ) {
          return false;
        }

        if (!searchText) {
          return true;
        }

        const haystack = [
          row?.id,
          row?.transmissionId,
          row?.downstreamReference,
          row?.receiptCode,
          row?.receiptMessage,
          row?.receiptStatus,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");

      });
  }, [records, filterApplied]);

  const getSortValue = (row, fieldName) => {
    switch (fieldName) {
      case "receivedAt":
      case "createdAt": {
        const timestamp = new Date(row?.[fieldName] || 0).getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
      }
      default:
        return row?.[fieldName] ?? "";
    }
  };

  const compareValues = (leftValue, rightValue) => {
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue - rightValue;
    }

    return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((left, right) => {
      const result = compareValues(
        getSortValue(left, orderBy),
        getSortValue(right, orderBy),
      );

      if (result !== 0) {
        return order === "asc" ? result : -result;
      }

      if (orderBy !== "receivedAt") {
        return (
          new Date(right?.receivedAt || 0).getTime() -
          new Date(left?.receivedAt || 0).getTime()
        );
      }

      return 0;
    });
  }, [filteredRecords, orderBy, order]);

  const totalCount = sortedRecords.length;
  const pagedRows = useMemo(() => {
    const start = pageNumber * recordsPerPage;
    return sortedRecords.slice(start, start + recordsPerPage);
  }, [sortedRecords, pageNumber, recordsPerPage]);

  const loadReceipts = async () => {
    if (!clientId) {
      setRecords([]);
      return;
    }

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "OrderTransmissionReceipt",
        action: "get",
        filter: { clientId },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setRecords(results.data);
      } else {
        setRecords([]);
        showMessage(results?.message || "Failed to load receipts", "error");
      }
    } catch (err) {
      setRecords([]);
      showMessage(err?.message || "Failed to load receipts", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const normalizedStart = filterDraft.startDate || null;
    const normalizedEnd = filterDraft.endDate || null;

    if (
      normalizedStart &&
      normalizedEnd &&
      new Date(normalizedStart) > new Date(normalizedEnd)
    ) {
      showMessage("From date cannot be after To date", "error");
      return;
    }

    setPageNumber(0);
    setFilterApplied({
      searchText: String(filterDraft.searchText || "").trim(),
      status: filterDraft.status || "all",
      startDate: normalizedStart,
      endDate: normalizedEnd,
    });
  };

  const clearFilters = () => {
    const cleared = {
      searchText: "",
      status: "all",
      startDate: null,
      endDate: null,
    };
    setFilterDraft(cleared);
    setFilterApplied(cleared);
    setPageNumber(0);
  };

  const handleSortChange = (property, nextOrder) => {
    setOrderBy(property);
    setOrder(nextOrder);
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "refresh") {
      loadReceipts();
    }
  };

  const statusItems = [
    { id: "all", name: "All" },
    { id: "accepted", name: "Accepted" },
    { id: "rejected", name: "Rejected" },
    { id: "pending", name: "Pending" },
    { id: "error", name: "Error" },
  ];

  const columns = [
    {
      name: "receiptStatus",
      label: "Status",
      width: "130px",
    },
    {
      name: "downstreamReference",
      label: "Downstream Ref",
      width: "220px",
      render: (row) => row?.downstreamReference || "",
    },
    {
      name: "transmissionId",
      label: "Transmission ID",
      width: "220px",
      render: (row) => row?.transmissionId || "",
    },
    {
      name: "receiptCode",
      label: "Code",
      width: "160px",
      render: (row) => row?.receiptCode || "",
    },
    {
      name: "receiptMessage",
      label: "Message",
      width: "360px",
      render: (row) => row?.receiptMessage || "",
    },
    {
      name: "receivedAt",
      label: "Received At",
      width: "170px",
      render: (row) =>
        row?.receivedAt ? new Date(row.receivedAt).toLocaleString() : "",
    },
    {
      name: "createdAt",
      label: "Created",
      width: "170px",
      render: (row) =>
        row?.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    },
  ];

  return (
    <Page
      pageName="Order Receipts"
      entityName={clientName || ""}
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "back", label: "Back", iconName: "ArrowBack" },
        { name: "refresh", label: "Refresh", iconName: "Refresh" },
      ]}
    >
      {!clientId ? (
        <Typography>Client context is missing for this page.</Typography>
      ) : (
        <Box>
          <Typography sx={{ mb: 1 }}>
            Review downstream acknowledgements and rejection messages.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr auto auto",
              gap: 1,
              alignItems: "end",
              mb: 1,
            }}
          >
            <Input
              label="Search"
              value={filterDraft.searchText}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  searchText: value,
                }))
              }
            />

            <Select
              label="Status"
              value={filterDraft.status}
              items={statusItems}
              onChange={(value) =>
                setFilterDraft((previous) => ({ ...previous, status: value }))
              }
            />

            <DateField
              label="From Date"
              value={filterDraft.startDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  startDate: value,
                }))
              }
            />

            <DateField
              label="To Date"
              value={filterDraft.endDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({ ...previous, endDate: value }))
              }
            />

            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>

          <Pagination
            totalCount={totalCount}
            pageNumber={pageNumber}
            recordsPerPage={recordsPerPage}
            onPageChange={(newPage) => setPageNumber(newPage)}
            onRecordsPerPageChange={(newRowsPerPage) => {
              setPageNumber(0);
              setRecordsPerPage(Number(newRowsPerPage));
            }}
            styles={{ marginBottom: "6px" }}
          />

          {loading ? (
            <Typography>Loading receipts...</Typography>
          ) : (
            <EntityList
              entityList={pagedRows}
              columns={columns}
              tableHeight="calc(100vh - 250px)"
              onSortChange={handleSortChange}
              orderBy={orderBy}
              order={order}
            />
          )}
        </Box>
      )}
    </Page>
  );
};

export default OrdersReceiptsPage;
